import * as Sentry from "@sentry/react";

/**
 * Initialise Sentry for the SPA.
 *
 * No-ops unless VITE_SENTRY_DSN is set, so it's inert in local dev and in any
 * build without a configured DSN. The DSN is baked at build time (it's a public
 * value, safe to ship to the browser — like the backend's SENTRY_DSN).
 *
 * Call once, before React renders (see main.tsx).
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Capture a session replay only when an error happens (cheap, high-signal).
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Sample performance traces modestly to keep volume/cost in check.
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Don't ship PII (IP/cookies) by default.
    sendDefaultPii: false,
  });
}

/** Report an error to Sentry (no-op if Sentry wasn't initialised). */
export function captureException(error: unknown): void {
  Sentry.captureException(error);
}